/**
 * dfd.ts — formulário do DFD (F5).
 */

import type { components } from './generated/api'
import { api, apiLegado } from './http'

export type ConfiguracaoDFD = components['schemas']['ConfiguracaoDFD']
export type RegraModalidade = components['schemas']['RegraModalidade']
export type TipoObjeto = components['schemas']['TipoObjeto']
export type Opcao = components['schemas']['Opcao']

/** O rascunho é um dicionário livre: o formulário evolui sem quebrar o contrato. */
export type FormDataDFD = Record<string, string>

export function configuracaoDFD() {
  return api<ConfiguracaoDFD>('/api/v1/dfd/configuracao')
}

export function salvarRascunho(formData: FormDataDFD, licitacaoId: number | null) {
  return api<{ licitacao_id: number; form_data: FormDataDFD }>(
    '/api/v1/dfd/rascunho',
    { method: 'POST', body: { licitacao_id: licitacaoId, form_data: formData } },
  )
}

export function carregarRascunho(licitacaoId: number) {
  return api<{ licitacao_id: number; form_data: FormDataDFD }>(
    `/api/v1/dfd/rascunho/${licitacaoId}`,
  )
}

/** Enquadramento já escolhido no formulário: a sugestão REDIGE a hipótese
 *  selecionada, nunca escolhe outra. Sem isto, a sugestão de justificativa
 *  citava um inciso do art. 75 diferente do que o servidor marcou. */
export type EnquadramentoDFD = {
  tipo_contratacao_direta?: string
  hipotese_dispensa?: string
  inciso_dispensa?: string
  hipotese_inexigibilidade?: string
  hipotese_credenciamento?: string
  consta_pca?: string
}

export function sugerirCampo(
  campo: string,
  objetoContratacao: string,
  orgaoSolicitante: string,
  enquadramento: EnquadramentoDFD = {},
) {
  return api<{ sugestao: string }>('/api/v1/dfd/sugestao', {
    method: 'POST',
    body: {
      campo,
      objeto_contratacao: objetoContratacao,
      orgao_solicitante: orgaoSolicitante,
      ...enquadramento,
    },
  })
}

// ── Catálogos oficiais (busca manual de código) ───────────────────────────

export type Catalogo = 'catmat' | 'catser' | 'tabela_sus'
export type ItemCatalogo = components['schemas']['ItemCatalogo']
export type ResultadosCatalogo = components['schemas']['ResultadosCatalogo']

export function buscarCatalogo(termo: string, catalogo: Catalogo) {
  const q = new URLSearchParams({ termo, catalogo })
  return api<ResultadosCatalogo>(`/api/v1/dfd/catalogo?${q}`)
}

// ── PCA (Plano de Contratações Anual) ─────────────────────────────────────

export type ItemPCA = components['schemas']['ItemPCA']
export type ItensPCA = components['schemas']['ItensPCA']

export function itensPCA() {
  return api<ItensPCA>('/api/v1/pca/itens')
}

// ── Planilha de itens (rotas legadas, via apiLegado — ver http.ts) ────────

export interface ItemPlanilha {
  item?: number
  descricao: string
  unidade?: string
  quantidade?: number | string
  // O create_dfd lê `codigoItemCatalogo` ou `codigo_catmat` — inclusive para
  // serviços. As chaves *_catser existem só para exibição/compatibilidade.
  codigo_catmat?: string
  descricao_catmat?: string
  grupo_catmat?: string
  classe_catmat?: string
  codigo_catser?: string
  descricao_catser?: string
  grupo_catser?: string
  classe_catser?: string
  tipo_catalogo?: string
  valor_unitario?: number
  valor_total?: number
  pendente_busca?: boolean
  manual_selection?: boolean
}

export interface RespostaPlanilha {
  success: boolean
  items: ItemPlanilha[]
  total?: number
  processamento_completo?: boolean
  usar_processamento_lotes?: boolean
  tipo_planilha?: string
  error?: string
  detalhes?: string[]
  total_erros?: number
}

export function extrairPlanilha(
  arquivo: File,
  tipoPlanilha: 'material' | 'servico',
  usarTabelaSus: boolean,
) {
  const form = new FormData()
  form.append('file', arquivo)
  form.append('tipo_planilha', tipoPlanilha)
  if (usarTabelaSus) form.append('usar_tabela_sus', 'on')
  return apiLegado<RespostaPlanilha>('/api/dfd/extract_planilha', {
    method: 'POST',
    form,
  })
}

/** Busca de códigos em blocos (o backend limita o processamento síncrono). */
export function buscarCodigosLote(itens: ItemPlanilha[], usarTabelaSus: boolean) {
  return apiLegado<{ success: boolean; items: ItemPlanilha[] }>(
    '/api/dfd/buscar_codigos_lote',
    { method: 'POST', body: { items: itens, usar_tabela_sus: usarTabelaSus } },
  )
}

// ── DFD gerado: visualização e edição ─────────────────────────────────────

export type DocumentoDFD = components['schemas']['DocumentoDFD']
export type SecaoDFD = components['schemas']['SecaoDFD']
export type FormularioDFDGerado = components['schemas']['FormularioDFD']
export type EdicaoDFDInput = components['schemas']['EdicaoDFDInput']

export function documentoDFD(id: number) {
  return api<DocumentoDFD>(`/api/v1/dfd/${id}`)
}

export function formularioDFD(id: number) {
  return api<FormularioDFDGerado>(`/api/v1/dfd/${id}/formulario`)
}

export function salvarDFD(id: number, dados: EdicaoDFDInput) {
  return api<DocumentoDFD>(`/api/v1/dfd/${id}`, { method: 'PUT', body: dados })
}

export function excluirDFD(id: number) {
  return api<{ processo_removido: boolean }>(`/api/v1/dfd/${id}`, {
    method: 'DELETE',
  })
}
